import { useMemo } from "react";
import Fuse, { IFuseOptions } from "fuse.js";

/**
 * Filters a list of items using a fuzzy search over a set of specified keys.
 * e.g., useFuzzySearch(items, "title", "searchQuery") will filter by "searchQuery" in the "title" property of each item.
 */
export const useFuzzySearch = <T>(
  items: T[],
  searchKeys: keyof T | (keyof T)[],
  searchQuery: string,
  options?: Partial<IFuseOptions<T>>
): T[] => {
  return useMemo(() => {
    if (!searchQuery.trim()) {
      return items;
    }

    const fuseOptions: IFuseOptions<T> = {
      keys: Array.isArray(searchKeys)
        ? (searchKeys as string[])
        : [searchKeys as string],
      threshold: 0.4,
      includeScore: false,
      ...options,
    };

    const fuse = new Fuse(items, fuseOptions);
    const results = fuse.search(searchQuery);
    return results.map((result) => result.item);
  }, [items, searchKeys, searchQuery, options]);
};
